import { Link } from 'react-router-dom';
import { DiaryLine } from './DiaryLine';
import { Waitlist } from './Waitlist';
import { tagColor, tagBackgroundColor } from '@/utils/tagTheme';
import { APP_STORE_URL, MAC_APP_STORE_URL } from '@/constants';

type Status = 'released' | 'cooking' | 'coming-soon';

interface PlatformItem {
  label: string;
  status: Status;
  /** External store link for shipped native apps. */
  href?: string;
  /** Internal route for the web client. */
  to?: string;
}

// Shipped first, then in-progress: with the fade mask below, the list
// visually trails off into the future.
const items: PlatformItem[] = [
  { label: 'iphone', status: 'released', href: APP_STORE_URL },
  { label: 'macos', status: 'released', href: MAC_APP_STORE_URL },
  { label: 'web', status: 'released', to: '/diaries' },
  { label: 'ipad', status: 'cooking' },
  { label: 'android', status: 'coming-soon' },
];

function StatusChip({ status }: { status: Status }) {
  return (
    <span
      className="whitespace-nowrap rounded-sm font-mono text-xs font-medium"
      style={{
        color: tagColor(status, 'light'),
        backgroundColor: tagBackgroundColor(status, 'light'),
        padding: '0 0.3em',
      }}
    >
      #{status}
    </span>
  );
}

const labelLinkClassName =
  'text-foreground underline-offset-2 transition-colors hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';

function PlatformLabel({ item }: { item: PlatformItem }) {
  if (item.href) {
    return (
      <a href={item.href} target="_blank" rel="noopener noreferrer" className={labelLinkClassName}>
        {item.label}
      </a>
    );
  }
  if (item.to) {
    return (
      <Link to={item.to} className={labelLinkClassName}>
        {item.label}
      </Link>
    );
  }
  return <span className="text-foreground">{item.label}</span>;
}

// Gentle trail-off toward the not-yet-shipped end of the list. Bottoms out
// at 45% so the last items stay legible; fading to transparent hid them.
const FADE_MASK = 'linear-gradient(to bottom, black 45%, rgba(0, 0, 0, 0.45) 100%)';

export function Platforms() {
  return (
    <>
      <DiaryLine className="mt-20 sm:mt-28" time="18:30">
        <h2 className="text-2xl font-semibold tracking-tight text-foreground sm:text-[1.75rem]">
          on all your devices
        </h2>
      </DiaryLine>

      <DiaryLine className="mt-3">
        <p className="max-w-[58ch] text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg">
          the same experience everywhere
        </p>
      </DiaryLine>

      <DiaryLine className="mt-8">
        <div className="flex flex-col gap-10 sm:flex-row sm:items-center sm:gap-16 lg:gap-24">
          <div className="min-w-0 shrink-0" style={{ WebkitMaskImage: FADE_MASK, maskImage: FADE_MASK }}>
            <ul
              role="list"
              className="m-0 grid list-none grid-cols-[max-content_max-content] items-center gap-x-3 gap-y-1.5 pl-0 font-mono text-sm leading-[1.75] sm:gap-x-8"
            >
              {items.map((item) => (
                <li key={item.label} className="contents">
                  <PlatformLabel item={item} />
                  <StatusChip status={item.status} />
                </li>
              ))}
            </ul>
          </div>
          <Waitlist className="sm:min-w-0 sm:max-w-[560px] sm:flex-1" />
        </div>
      </DiaryLine>
    </>
  );
}

export default Platforms;
